/**
 * Utilidades para manejar variantes de productos
 * Este archivo contiene funciones para filtrar y seleccionar variantes de productos de forma dinámica
 */

/**
 * Calcula la jerarquía de variantes automáticamente basada en cardinalidad
 * Menor cardinalidad = más general (padre)
 * Mayor cardinalidad = más específico (hijo)
 */
function inferVariantHierarchy(products) {
  // Contar opciones únicas por cada variante
  const variantCardinalityMap = {};

  products.forEach(product => {
    if (product.variantData) {
      Object.entries(product.variantData).forEach(([key, value]) => {
        const normalizedKey = normalizeVariantKey(key);
        if (!variantCardinalityMap[normalizedKey]) {
          variantCardinalityMap[normalizedKey] = new Set();
        }
        variantCardinalityMap[normalizedKey].add(String(value));
      });
    }
  });

  // Convertir a array con cardinalidad y ordenar
  const variantsByCardinality = Object.entries(variantCardinalityMap)
    .map(([key, valueSet]) => ({
      key,
      cardinality: valueSet.size
    }))
    .sort((a, b) => a.cardinality - b.cardinality); // Menor primero

  // Crear jerarquía: cada variante tiene como padres a todas las anteriores
  const hierarchy = {};

  variantsByCardinality.forEach((variant, index) => {
    // Los padres son todas las variantes con menor cardinalidad
    const parents = variantsByCardinality
      .slice(0, index)
      .map(parent => parent.key);

    hierarchy[variant.key] = parents;
  });

  return hierarchy;
}

/**
 * Normaliza las keys de variantData de forma completamente genérica
 * Solo aplica normalización básica sin conocimiento específico de dominios
 */
function normalizeVariantKey(key) {
  return key.toLowerCase().trim();
}

/**
 * Obtiene el título display para una key de variante de forma completamente dinámica
 */
function getDisplayTitle(key) {
  // Normalizar y capitalizar la primera letra
  const normalizedKey = normalizeVariantKey(key);
  return normalizedKey.charAt(0).toUpperCase() + normalizedKey.slice(1) + ':';
}

/**
 * Obtiene las variantes padre para una variante específica usando jerarquía dinámica
 * Solo incluye las selecciones de variantes que son "padre" de la variante dada
 */
function getParentVariants(variantKey, selectedVariants, hierarchy) {
  const parentKeys = hierarchy[variantKey] || [];
  const parentVariants = {};

  parentKeys.forEach(parentKey => {
    if (selectedVariants[parentKey]) {
      parentVariants[parentKey] = selectedVariants[parentKey];
    }
  });

  return parentVariants;
}

/**
 * Verifica si un producto coincide con una variante específica de forma completamente genérica
 */
export function matchesVariant(variantData, key, value) {
  if (!variantData) return false;

  const normalizedKey = normalizeVariantKey(key);
  const normalizedValue = String(value).toLowerCase().trim();

  // Obtener valores del variantData y normalizarlos
  const productValue1 = variantData[normalizedKey] ? String(variantData[normalizedKey]).toLowerCase().trim() : null;
  const productValue2 = variantData[key] ? String(variantData[key]).toLowerCase().trim() : null;

  // Buscar con la key normalizada primero, luego con la original como fallback
  const matches = productValue1 === normalizedValue || productValue2 === normalizedValue;

  return matches;
}

/**
 * Extrae todas las opciones de variantes de un grupo de productos
 */
export function extractVariantOptions(products) {
  const keyValueMap = {};

  products.forEach(product => {
    if (product.variantData) {
      Object.entries(product.variantData).forEach(([key, value]) => {
        const normalizedKey = normalizeVariantKey(key);

        if (!keyValueMap[normalizedKey]) {
          keyValueMap[normalizedKey] = new Set();
        }
        keyValueMap[normalizedKey].add(String(value));
      });
    }
  });

  return Object.entries(keyValueMap).map(([normalizedKey, valueSet]) => ({
    title: getDisplayTitle(normalizedKey),
    key: normalizedKey,
    options: Array.from(valueSet).map((value, index) => ({
      id: `${normalizedKey}-${value.toLowerCase().replace(/\s+/g, '-')}`,
      name: value,
      selected: index === 0
    }))
  }));
}

/**
 * Filtra productos basado en las selecciones actuales de variantes
 */
export function getFilteredProducts(products, selectedVariants) {
  return products.filter(product => {
    return Object.entries(selectedVariants).every(([key, value]) => {
      if (!value) return true; // No hay selección para esta key
      return matchesVariant(product.variantData, key, value);
    });
  });
}

/**
 * Obtiene opciones de variantes filtradas basado en selecciones actuales
 * Aplica filtrado en cascada: cada variante filtra las opciones de las siguientes
 */
export function getFilteredVariantOptions(products, selectedVariants) {
  // Inferir jerarquía dinámica basada en los productos
  const hierarchy = inferVariantHierarchy(products);

  // Primero, obtener todas las variantes disponibles sin filtros
  const allVariantSelections = extractVariantOptions(products);

  // Aplicar filtrado direccional en cascada para cada variante
  const filteredSelections = allVariantSelections.map(selection => {
    // Para cada variante, filtrar solo por las variantes "padre" en la jerarquía dinámica
    const parentVariants = getParentVariants(selection.key, selectedVariants, hierarchy);

    // Filtrar productos basado solo en las variantes padre
    const filteredProducts = getFilteredProducts(products, parentVariants);

    // Extraer solo las opciones de esta variante específica
    const variantOptions = new Set();
    filteredProducts.forEach(product => {
      if (product.variantData) {
        Object.entries(product.variantData).forEach(([key, value]) => {
          const normalizedKey = normalizeVariantKey(key);
          if (normalizedKey === selection.key) {
            variantOptions.add(String(value));
          }
        });
      }
    });

    const options = Array.from(variantOptions).map((value, index) => ({
      id: `${selection.key}-${value.toLowerCase().replace(/\s+/g, '-')}`,
      name: value,
      selected: selectedVariants[selection.key] === value
    }));

    return {
      ...selection,
      options
    };
  });

  return filteredSelections;
}

/**
 * Encuentra el producto exacto que coincida con todas las variantes seleccionadas
 */
export function findProductByVariants(products, selectedVariants) {
  return products.find(product => {
    if (!product.variantData) return false;

    return Object.entries(selectedVariants).every(([key, value]) => {
      if (!value) return true; // No hay selección para esta key
      return matchesVariant(product.variantData, key, value);
    });
  });
}

/**
 * Valida y corrige selecciones de variantes para asegurar que sean válidas
 */
export function validateAndCorrectVariants(products, selectedVariants) {
  // Inferir jerarquía para procesar en orden correcto
  const hierarchy = inferVariantHierarchy(products);
  const allVariantSelections = extractVariantOptions(products);

  // Ordenar variantes por jerarquía (padres primero)
  const sortedVariants = allVariantSelections.sort((a, b) => {
    const aParents = hierarchy[a.key]?.length || 0;
    const bParents = hierarchy[b.key]?.length || 0;
    return aParents - bParents;
  });

  const correctedVariants = {};

  // Procesar cada variante en orden jerárquico
  sortedVariants.forEach(selection => {
    const currentSelection = selectedVariants[selection.key];

    // Obtener opciones disponibles basadas en las variantes ya corregidas
    const availableOptions = getFilteredVariantOptions(products, correctedVariants);
    const currentVariantOptions = availableOptions.find(s => s.key === selection.key);

    if (currentVariantOptions) {
      // Si la selección actual es válida en el contexto actual, mantenerla
      if (currentSelection && currentVariantOptions.options.some(opt => opt.name === currentSelection)) {
        correctedVariants[selection.key] = currentSelection;
      } else {
        // Si no es válida, usar la primera opción disponible
        if (currentVariantOptions.options.length > 0) {
          correctedVariants[selection.key] = currentVariantOptions.options[0].name;
        }
      }
    }
  });
  return correctedVariants;
}

/**
 * Obtiene el estado inicial de variantes para un grupo de productos
 */
export function getInitialVariantState(products) {
  if (products.length === 0) return {};

  const variantSelections = extractVariantOptions(products);
  const initialState = {};

  variantSelections.forEach(selection => {
    if (selection.options.length > 0) {
      initialState[selection.key] = selection.options[0].name;
    }
  });

  return initialState;
}
